import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import "../styles/PyramidBoard.css";
import {
  CharacterCount,
  defaultCharactersCount,
  defaultGameStyle,
  GameStyle,
} from "../Setting_difficulty";

interface Character {
  id: string;
  size: number;
  svgUrl: string;
  originalUrl: string;
}

type Area = "A" | "B" | "C";
type MoveButtonType = "AB" | "AC" | "BC" | "BA" | "CA" | "CB" | "next" | null;

interface BoardProps {
  GameMode: "Control" | "Edit" | "Debug" | "Run";
  MoveButton: MoveButtonType;
  setMoveButton: (value: MoveButtonType) => void;
  setMessage: (value: string) => void;
  Message: string;
  gameStyle?: GameStyle;
  characterCount?: CharacterCount;
}

const Board: React.FC<BoardProps> = ({
  GameMode,
  MoveButton,
  setMoveButton,
  setMessage,
  gameStyle,
  characterCount,
}) => {
  const [programText, setProgramText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isGameWon, setIsGameWon] = useState(false);
  const [isProgramFinished, setIsProgramFinished] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [prevGameMode, setPrevGameMode] = useState(GameMode);
  const [currentLine, setCurrentLine] = useState<number>(-1);
  const [errorLine, setErrorLine] = useState<number>(-1);
  const [debugCurrentLine, setDebugCurrentLine] = useState<number>(0);
  const [animatingCharacter, setAnimatingCharacter] = useState<string | null>(
    null
  );
  const [appearingCharacter, setAppearingCharacter] = useState<string | null>(
    null
  );

  const executionRef = useRef({
    isExecuting: false,
    shouldStop: false,
  });

  const selectedGameStyle = gameStyle ?? defaultGameStyle;
  const selectedCharacterCount = characterCount ?? defaultCharactersCount;
  const getAllImages = useCallback(() => {
  const images = new Set<string>();
  
  // Фон
  images.add(`/img/${selectedGameStyle}/fon.svg`);
  
  // Финальный фон (для победы)
  images.add(`/img/${selectedGameStyle}/final.svg`);
  
  // Изображения персонажей/дисков
  for (let i = 1; i <= selectedCharacterCount; i++) {
    // Основные изображения
    images.add(`/img/${selectedGameStyle}/characters/${i}.svg`);
    
    // Дополнительные состояния (если есть)
    if (selectedGameStyle === "animal") {
      if (i === 3) {
        images.add(`/img/animal/characters/3_fail.svg`);
      }
      if (i === 4 && selectedCharacterCount === 4) {
        images.add(`/img/animal/characters/4_win.svg`);
      }
    }
  }
  
  return Array.from(images);
}, [selectedGameStyle, selectedCharacterCount]);
useEffect(() => {
  const loadImages = async () => {
    const images = getAllImages();

    const loadPromises = images.map((url) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.src = url;

        img.onload = () => {
          resolve();
        };

        img.onerror = () => {
          console.warn(`Не удалось загрузить: ${url}`);
          resolve();
        };
      });
    });

    try {
      await Promise.all(loadPromises);

      // Небольшая задержка для плавности
      await new Promise((resolve) => setTimeout(resolve, 300));

      setIsLoading(false);
    } catch (error) {
      console.error("Ошибка загрузки изображений:", error);
      setIsLoading(false);
    }
  };

  loadImages();
}, [selectedGameStyle, selectedCharacterCount, getAllImages]);
  const getCharacterUrls = (gameStyle: string, size: number) => ({
    svgUrl: `/img/${gameStyle}/characters/${size}.svg`,
    originalUrl: `/img/${gameStyle}/characters/${size}.svg`,
  });
  // Инициализация платформ с персонажами
  const initialPlatformArrays = useMemo(() => {
    const arrays: Record<Area, Character[]> = {
      A: [],
      B: [],
      C: [],
    };

    for (let i = 0; i < selectedCharacterCount; i++) {
      arrays.A.push({
        id: `character${i + 1}`,
        size: i + 1,
        ...getCharacterUrls(selectedGameStyle, i + 1)
      });
    }

    return arrays;
  }, [selectedCharacterCount, selectedGameStyle]);

  const [platformArrays, setPlatformArrays] = useState<
    Record<Area, Character[]>
  >(initialPlatformArrays);

  // Сброс состояния при изменении количества персонажей
  useEffect(() => {
    const newArrays: Record<Area, Character[]> = {
      A: [],
      B: [],
      C: [],
    };

    for (let i = 0; i < selectedCharacterCount; i++) {
      newArrays.A.push({
        id: `character${i + 1}`,
        size: i + 1,
         ...getCharacterUrls(selectedGameStyle, i + 1)
      });
    }

    setPlatformArrays(newArrays);
    resetGameState();
  }, [selectedCharacterCount, selectedGameStyle]);

  // Сброс игрового состояния
  const resetGameState = useCallback(() => {
    setIsGameWon(false);
    setIsProgramFinished(false);
    setHasError(false);
    setMessage("");
    setCurrentLine(-1);
    setErrorLine(-1);
    setDebugCurrentLine(0); // Только для Debug режима
    executionRef.current.shouldStop = true;
    executionRef.current.isExecuting = false;
  }, [setMessage]);

  // Замена изображения персонажа
  const replaceCharacterImage = useCallback(
    (size: number, newImageUrl: string) => {
      setPlatformArrays((prev) => {
        const newState = { ...prev };
        Object.keys(newState).forEach((area) => {
          newState[area as Area] = newState[area as Area].map((character) =>
            character.size === size
              ? { ...character, svgUrl: newImageUrl }
              : character
          );
        });
        return newState;
      });
    },
    []
  );

  // Восстановление оригинальных изображений
  const restoreOriginalImages = useCallback(() => {
    setPlatformArrays((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((area) => {
        newState[area as Area] = newState[area as Area].map((character) => ({
          ...character,
          svgUrl: character.originalUrl,
        }));
      });
      return newState;
    });
  }, []);

  // Обновление изображений при изменении состояния игры
  useEffect(() => {
    if (
      gameStyle === "animal"
    ) {
      console.log(hasError);
      if (isGameWon && selectedCharacterCount === 4) {
        replaceCharacterImage(
          4,
          `/img/${selectedGameStyle}/characters/4_win.svg`
        );
      } else if (hasError && selectedCharacterCount >= 3) {
        replaceCharacterImage(
          3,
          `/img/${selectedGameStyle}/characters/3_fail.svg`
        );
      } else if (isProgramFinished && selectedCharacterCount >= 3&& !isGameWon) {
        replaceCharacterImage(
          3,
          `/img/${selectedGameStyle}/characters/3_fail.svg`
        );
      }
    }
  }, [
    isGameWon,
    isProgramFinished,
    hasError,
    gameStyle,
    selectedCharacterCount,
    replaceCharacterImage,
    selectedGameStyle,
  ]);

  // Основная функция перемещения персонажа
  const moveCharacter = useCallback(
    (
      from: Area,
      to: Area,
      isLastCommand: boolean = false
    ): Promise<"success" | "empty" | "sizeError" | "win" | "towerB"> => {
      return new Promise((resolve) => {
        setPlatformArrays((prev) => {
          const fromArray = [...prev[from]];
          const toArray = [...prev[to]];

          // Проверка на пустую платформу
          if (fromArray.length === 0) {
            resolve("empty");
            return prev;
          }

          const characterToMove = fromArray[fromArray.length - 1];

          // Проверка правил размещения
          if (toArray.length > 0) {
            const topCharacterOnTarget = toArray[toArray.length - 1];
            if (characterToMove.size < topCharacterOnTarget.size) {
              resolve("sizeError");
              return prev;
            }
          }

          // Анимация для режима Run
          if (GameMode === "Run") {
            // Фаза 1: Исчезновение с исходной платформы
            setAnimatingCharacter(characterToMove.id);

            setTimeout(() => {
              // Убираем персонажа с исходной платформы
              const movedCharacter = fromArray.pop();

              // Фаза 2: Появление на целевой платформе
              toArray.push(movedCharacter!);
              const newState = {
                ...prev,
                [from]: fromArray,
                [to]: toArray,
              };

              setPlatformArrays(newState);

              // Запускаем анимацию появления
              setAppearingCharacter(characterToMove.id);

              // Убираем анимацию исчезновения
              setAnimatingCharacter(null);

              // Через время убираем анимацию появления
              setTimeout(() => {
                setAppearingCharacter(null);

                // Проверяем условия победы после завершения анимации
                if (
                  to === "C" &&
                  newState.C.length === selectedCharacterCount
                ) {
                  resolve("win");
                } else if (
                  to === "B" &&
                  newState.B.length === selectedCharacterCount &&
                  isLastCommand
                ) {
                  resolve("towerB");
                } else {
                  resolve("success");
                }
              }, 300); // Длительность анимации появления
            }, 500); // Длительность анимации исчезновения

            return prev;
          }

          // Мгновенное перемещение для других режимов
          const movedCharacter = fromArray.pop();
          toArray.push(movedCharacter!);
          const newState = {
            ...prev,
            [from]: fromArray,
            [to]: toArray,
          };

          if (to === "C" && newState.C.length === selectedCharacterCount) {
            resolve("win");
          } else if (
            to === "B" &&
            newState.B.length === selectedCharacterCount &&
            isLastCommand
          ) {
            resolve("towerB");
          } else {
            resolve("success");
          }

          return newState;
        });
      });
    },
    [selectedCharacterCount, GameMode]
  );
  // Добавление команды в программу
  const handleAddCommand = useCallback(
    (command: string) => {
      if (GameMode === "Edit" && !isGameWon) {
        setProgramText((prev) => (prev ? prev + "\n" + command : command));
      }
    },
    [GameMode, isGameWon]
  );

  // Выполнение следующей строки в режиме Debug
  const executeNextLine = useCallback(async () => {
    if (programText.trim() === "") {
      setMessage("Программы нет");
      return;
    }

    if (isGameWon) {
      setMessage("Игра уже завершена!");
      return;
    }

    const lines = programText.split("\n").filter((line) => line.trim() !== "");

    if (debugCurrentLine === lines.length) {
      setMessage("Конец программы");
      setCurrentLine(-1);
      setIsProgramFinished(true);
      return;
    }

    setCurrentLine(debugCurrentLine);
    const line = lines[debugCurrentLine].trim();

    // Парсинг команды move
    const moveMatch = line.match(/move\(([ABC]),([ABC])\)/i);
    if (!moveMatch) {
      setMessage(`Неверная команда: "${line}"`);
      setErrorLine(debugCurrentLine);
      setHasError(true);
      return;
    }

    const from = moveMatch[1] as Area;
    const to = moveMatch[2] as Area;
    const isLastCommand = debugCurrentLine === lines.length - 1;

    try {
      const result = await moveCharacter(from, to, isLastCommand);

      switch (result) {
        case "empty":
          setMessage(`Ошибка: платформа ${from} пуста`);
          setErrorLine(debugCurrentLine);
          setHasError(true);
          break;
        case "sizeError":
          setMessage(
            `Ошибка: нельзя поместить больший диск на меньший (${from} -> ${to})`
          );
          setErrorLine(debugCurrentLine);
          setHasError(true);
          break;
        case "win":
          setMessage("Задание выполнено! Ты можешь запустить программу");
          setIsGameWon(true);
          setIsProgramFinished(true);
          setCurrentLine(-1);
          break;
        case "towerB":
          setMessage("Молодец, но необходимо собрать на площадке C");
          setErrorLine(debugCurrentLine);
          setHasError(true);
          break;
        case "success":
          setMessage(`Выполнено: ${line}`);
          setDebugCurrentLine((prev) => prev + 1);
          if (isLastCommand) {
            setIsProgramFinished(true);
          }
          break;
      }
    } catch (error) {
      setMessage("Ошибка при выполнении команды");
      setErrorLine(debugCurrentLine);
      setHasError(true);
    }
  }, [
    programText,
    isGameWon,
    debugCurrentLine,
    moveCharacter,
    setMessage,
    setIsGameWon,
  ]);
  // Обработка условия победы
  const handleWinCondition = useCallback(
    (programLength: number) => {
      let message = "Задание идеально выполнено! Молодец!";
      if (selectedCharacterCount === 2 && programLength > 3) {
        message =
          "Задание выполнено! Можно решить за меньшее количество шагов (3)";
      } else if (selectedCharacterCount === 3 && programLength > 7) {
        message =
          "Задание выполнено! Можно решить за меньшее количество шагов (7)";
      } else if (selectedCharacterCount === 4 && programLength > 15) {
        message =
          "Задание выполнено! Можно решить за меньшее количество шагов (15)";
      }

      setMessage(message);
      setIsGameWon(true);
      setIsProgramFinished(true);
      setCurrentLine(-1); // Сбрасываем подсветку текущей строки
    },
    [selectedCharacterCount]
  );

  // Выполнение всей программы в режиме Run
  const executeProgram = useCallback(async () => {
    if (executionRef.current.isExecuting) {
      return;
    }

    executionRef.current.isExecuting = true;
    executionRef.current.shouldStop = false;

    // Сохраняем текущие значения перед сбросом
    const currentProgramText = programText;

    // Сбрасываем только игровое состояние, но не позицию выполнения
    setIsGameWon(false);
    setIsProgramFinished(false);
    setHasError(false);
    setMessage("Выполнение программы");
    setCurrentLine(-1);
    setErrorLine(-1);

    if (currentProgramText.trim() === "") {
      setMessage("Программы нет");
      executionRef.current.isExecuting = false;
      return;
    }

    const lines = currentProgramText
      .split("\n")
      .filter((line) => line.trim() !== "");

    try {
      // Используем отдельную переменную для итерации, а не debugCurrentLine
      let currentIndex = 0;

      for (currentIndex = 0; currentIndex < lines.length; currentIndex++) {
        if (executionRef.current.shouldStop || GameMode !== "Run") {
          break;
        }

        setCurrentLine(currentIndex);
        const line = lines[currentIndex].trim();

        const moveMatch = line.match(/move\(([ABC]),([ABC])\)/i);
        if (!moveMatch) {
          setMessage(`Неверная команда: "${line}"`);
          setErrorLine(currentIndex);
          setHasError(true);
          executionRef.current.shouldStop = true;
          break;
        }

        const from = moveMatch[1] as Area;
        const to = moveMatch[2] as Area;
        const isLastCommand = currentIndex === lines.length - 1;

        const result = await moveCharacter(from, to, isLastCommand);

        switch (result) {
          case "empty":
            setMessage(`Ошибка: платформа ${from} пуста`);
            setErrorLine(currentIndex);
            setHasError(true);
            executionRef.current.shouldStop = true;
            break;
          case "sizeError":
            setMessage(
              `Ошибка: нельзя поместить больший диск на меньший (${from} -> ${to})`
            );
            setErrorLine(currentIndex);
            setHasError(true);
            executionRef.current.shouldStop = true;
            break;
          case "win":
            handleWinCondition(lines.length);
            executionRef.current.shouldStop = true;
            break;
          case "towerB":
            setMessage("Молодец, но необходимо собрать на площадке C");
            setErrorLine(currentIndex);
            setHasError(true);
            executionRef.current.shouldStop = true;
            break;
          case "success":
            // Успешное выполнение - продолжаем
            setMessage(`Выполнено: ${line}`);
            break;
        }

        // Добавляем задержку между командами только для успешных выполнений
        if (result === "success" && !executionRef.current.shouldStop) {
          await new Promise((resolve) => setTimeout(resolve, 800));
        }

        if (executionRef.current.shouldStop) break;
      }

      // Если дошли до конца без ошибок и не остановились
      if (
        !executionRef.current.shouldStop &&
        GameMode === "Run" &&
        currentIndex === lines.length
      ) {
        setMessage("Конец программы");
        setIsProgramFinished(true);
        setCurrentLine(-1);
        // Проверяем, собрана ли башня на C
        if (platformArrays.C.length === selectedCharacterCount) {
          setIsGameWon(true);
          setMessage("Задание выполнено успешно!");
        }
      }
    } catch (error) {
      console.error("Ошибка выполнения программы:", error);
      setMessage("Ошибка выполнения программы");
      setHasError(true);
    } finally {
      executionRef.current.isExecuting = false;
    }
  }, [
    programText,
    GameMode,
    moveCharacter,
    setMessage,
    selectedCharacterCount,
    handleWinCondition,
    platformArrays.C.length,
  ]);

  // Обработка результатов перемещения
  const handleMoveResult = useCallback(
    (
      result: "success" | "empty" | "sizeError" | "win" | "towerB",
      from: Area,
      to: Area
    ) => {
      switch (result) {
        case "empty":
          setMessage(`Ошибка: платформа ${from} пуста`);
          break;
        case "sizeError":
          setMessage(`Ошибка: нельзя поместить большего персонажа на меньшего`);
          break;
        case "win":
          setMessage("Поздравляю! Теперь напиши программу");
          setIsGameWon(true);
          break;
        case "towerB":
          setMessage("Молодец, но необходимо собрать на площадке C");
          break;
      }

      if (
        result !== "success" &&
        (GameMode === "Debug" || GameMode === "Run")
      ) {
        setHasError(true);
      }
    },
    [setMessage, setIsGameWon, GameMode]
  );

  // Обработка кнопок перемещения
  useEffect(() => {
    if (!MoveButton || isGameWon) return;
    setMessage("");

    if (MoveButton === "next") {
      if (GameMode === "Debug") {
        executeNextLine();
      }
      setMoveButton(null);
      return;
    }

    const from = MoveButton[0] as Area;
    const to = MoveButton[1] as Area;

    if (GameMode === "Control") {
      moveCharacter(from, to).then((result) => {
        handleMoveResult(result, from, to);
        setMoveButton(null);
      });
    } else if (GameMode === "Edit") {
      handleAddCommand(`move(${from},${to})`);
      setMoveButton(null);
    }
  }, [
    MoveButton,
    GameMode,
    isGameWon,
    moveCharacter,
    setMessage,
    setMoveButton,
    handleMoveResult,
    executeNextLine,
    handleAddCommand,
  ]);

  // Обработка изменения режима игры
  useEffect(() => {
    if (prevGameMode !== GameMode) {
      restoreOriginalImages();
      resetGameState();
      setPlatformArrays(initialPlatformArrays);
      setPrevGameMode(GameMode);
    }

    if (GameMode === "Run" && prevGameMode !== "Run") {
      executeProgram();
    }

    // Остановка выполнения при выходе из режима Run
    if (prevGameMode === "Run" && GameMode !== "Run") {
      executionRef.current.shouldStop = true;
    }
  }, [
    GameMode,
    prevGameMode,
    selectedCharacterCount,
    initialPlatformArrays,
    executeProgram,
    restoreOriginalImages,
    resetGameState,
  ]);

  // Рендер программы
  const renderProgram = useCallback(() => {
    if (programText.trim() === "") {
      return <div className="Pyramid-program-empty">Программа пуста</div>;
    }

    const lines = programText.split("\n").filter((line) => line.trim() !== "");

    return lines.map((line, index) => {
      let className = "Pyramid-program-line";
      if (index === currentLine) className += " current-line";
      if (index === errorLine) className += " error-line";

      return (
        <div key={index} className={className}>
          {line}
        </div>
      );
    });
  }, [programText, currentLine, errorLine]);

  // Рендер персонажей
  const renderCharacters = useCallback(
    (characters: Character[]) => {
      return characters.map((character) => {
        const isAnimating = animatingCharacter === character.id;
        const isAppearing = appearingCharacter === character.id;

        const animationClass = isAnimating
          ? "fade-out"
          : isAppearing
          ? "fade-in"
          : "";
        const shouldShowWinPers =
          selectedGameStyle === "animal" &&
          character.size === 4 &&
          isGameWon === true;

        return (
          <div
            key={character.id}
            className={`Pyramid-case-character-${character.size} ${
              selectedGameStyle === "acrobat" ? "acrobat" : ""
            }`}
          >
            <img
              src={character.svgUrl}
              alt={`Персонаж ${character.size}`}
              className={`Pyramid-character Pyramid-size-${
                character.size
              } ${animationClass} ${
                selectedGameStyle === "acrobat" ? "acrobat" : ""
              } ${shouldShowWinPers ? "win-pers" : ""}`}
            />
          </div>
        );
      });
    },
    [animatingCharacter, appearingCharacter, selectedGameStyle, isGameWon] // Добавьте isGameWon в зависимости
  );

  return (
    <div className="Pyramid-Board">
      <div className="Pyramid-Scene">
         {isLoading?<div className="board-loading">
            <div className="loading-text">Загрузка...</div>
          </div>: <>
        <img
          className="Pyramid-BackgroundImg"
          src={`/img/${selectedGameStyle}/fon.svg`}
          alt="Фон"
        />
        {isGameWon && (
          <img
            className="Pyramid-winbackground"
            src={`/img/${selectedGameStyle}/final.svg`}
            alt="Победа"
            style={{
              right: selectedGameStyle === "acrobat" ? "7.5vw" : "",
            }}
          />
        )}
        <div
          className={`Pyramid-platform Pyramid-A ${
            selectedGameStyle === "acrobat" ? "acrobat" : ""
          }`}
        >
          {renderCharacters(platformArrays.A)}
        </div>
        <div
          className={`Pyramid-platform Pyramid-B ${
            selectedGameStyle === "acrobat" ? "acrobat" : ""
          }`}
        >
          {renderCharacters(platformArrays.B)}
        </div>
        <div
          className={`Pyramid-platform Pyramid-C ${
            selectedGameStyle === "acrobat" ? "acrobat" : ""
          }`}
        >
          {renderCharacters(platformArrays.C)}
        </div></> }
      </div>

      <div className="Pyramid-Management-bar">
        {GameMode === "Run" || GameMode === "Debug" ? (
          <div className="Pyramid-console run">{renderProgram()}</div>
        ) : (
          <textarea
            className="Pyramid-console edit"
            value={programText}
            onChange={(e) => setProgramText(e.target.value)}
            style={{
              background: GameMode === "Edit" ? "rgb(227, 241, 250)" : "white",
              pointerEvents: GameMode !== "Edit" ? "none" : "auto",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Board;
